import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { EmployeePracticeAreaSource } from '../../../models/resource.models';
import { PracticeArea } from '../../../models/user.models';
import { describeApiError, toApiError } from '../../../utils/api-error.utils';
import { AuthService } from '../../../services/auth.service';
import { BreadcrumbService } from '../../../services/breadcrumb.service';
import { PracticeAreaService } from '../../../services/practice-area.service';
import { BreadcrumbComponent } from '../../shared/breadcrumb/breadcrumb.component';
import {
  ResourceMembershipDataSource,
  ResourceMembershipPanelComponent,
} from '../shared/resource-membership-panel/resource-membership-panel.component';

@Component({
  selector: 'app-practice-area-details',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    RouterModule,
    BreadcrumbComponent,
    ResourceMembershipPanelComponent,
  ],
  templateUrl: './practice-area-details.component.html',
  styleUrls: ['./practice-area-details.component.scss'],
})
export class PracticeAreaDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private practiceAreaService = inject(PracticeAreaService);
  private breadcrumbService = inject(BreadcrumbService);
  private authService = inject(AuthService);

  practiceArea: PracticeArea | null = null;
  isLoading = false;
  error: string | null = null;
  practiceAreaCode: string | null = null;

  /** Drives the shared resource-membership panel for this practice area's members. */
  practiceAreaDataSource: ResourceMembershipDataSource | null = null;

  // Inline title editing
  isEditingTitle = false;
  editedTitle: string = '';
  isSavingTitle = false;
  titleError: string | null = null;

  /**
   * Matches `UpdateDisplayNameRequest`'s `[MaxLength(200)]` and the create control's own cap, so an
   * over-length name cannot be submitted. Keep the three in step: the API rejects past this with a
   * validation 400, whose message the error branch below shows.
   */
  readonly maxNameLength = 200;
  readonly titleInput = viewChild<ElementRef<HTMLInputElement>>('titleInput');

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      const code = params['id'];
      if (code) {
        this.practiceAreaCode = code;
        this.practiceAreaDataSource = this.buildPracticeAreaDataSource(code);
        this.loadPracticeArea();
      } else {
        this.error = 'No practice area code provided';
      }
    });
  }

  private loadPracticeArea(): void {
    if (!this.practiceAreaCode) return;

    this.isLoading = true;
    this.error = null;

    this.practiceAreaService
      .getPracticeAreaByCode(this.practiceAreaCode)
      .subscribe({
        next: (practiceArea) => {
          this.practiceArea = practiceArea;
          this.breadcrumbService.setDynamicLabel(
            ':practiceAreaId',
            practiceArea.description,
          );
          this.editedTitle = practiceArea.description || '';
          this.isLoading = false;
        },
        error: (error) => {
          this.error = describeApiError(error, 'Failed to load practice area');
          this.isLoading = false;
        },
      });
  }

  retry(): void {
    this.loadPracticeArea();
  }

  // --- Inline title editing ---

  startEditingTitle(): void {
    if (!this.practiceArea) return;
    this.isEditingTitle = true;
    this.editedTitle = this.practiceArea.description;
    this.titleError = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const titleInput = this.titleInput();
    if (this.isEditingTitle && titleInput) {
      const titleEditor = titleInput.nativeElement.closest('.title-editor');
      const clickedEditableTitle = (event.target as HTMLElement).closest(
        '.editable-title',
      );

      // Don't close if clicking the editable title to start editing
      if (clickedEditableTitle) {
        return;
      }

      // Close if clicking outside the editor
      if (titleEditor && !titleEditor.contains(event.target as Node)) {
        this.saveTitle();
      }
    }
  }

  cancelEditingTitle(): void {
    if (!this.practiceArea) return;
    this.isEditingTitle = false;
    this.editedTitle = this.practiceArea.description;
    this.titleError = null;
  }

  onTitleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveTitle();
    } else if (event.key === 'Escape') {
      this.cancelEditingTitle();
    }
  }

  saveTitle(): void {
    if (!this.practiceAreaCode || !this.practiceArea || this.isSavingTitle)
      return;

    const trimmedTitle = this.editedTitle.trim();
    const currentDescription = this.practiceArea.description;
    const originalDescription =
      this.practiceArea.originalDescription || currentDescription;

    // If title hasn't changed, just cancel editing (no save needed)
    if (trimmedTitle === currentDescription) {
      this.cancelEditingTitle();
      return;
    }

    // If cleared and enter pressed
    if (!trimmedTitle) {
      // If there's a custom display name (description !== originalDescription)
      if (currentDescription !== originalDescription) {
        // Send null to clear the override
        this.updateDisplayName(null);
      } else {
        // No override exists, just restore and cancel editing
        this.cancelEditingTitle();
      }
      return;
    }

    // Title has changed, save it
    this.updateDisplayName(trimmedTitle);
  }

  private updateDisplayName(displayName: string | null): void {
    if (!this.practiceAreaCode) return;

    this.isSavingTitle = true;
    this.titleError = null;

    this.practiceAreaService
      .updatePracticeAreaDisplayName(this.practiceAreaCode, displayName)
      .subscribe({
        next: () => {
          this.isSavingTitle = false;
          this.isEditingTitle = false;
          // Reload the practice area to get the updated description
          this.loadPracticeArea();
        },
        error: (error) => {
          this.isSavingTitle = false;

          // This message sits in the name field's error slot, so only the duplicate-name 409 is
          // shown verbatim: it names the practice area holding the name ("...already in use by
          // practice area 1234."), which is the part an admin needs in order to go and fix it, and
          // the create control renders the server message too, so both paths agree. A validation
          // 400 cannot come from this control (the input enforces maxNameLength), so it gets the
          // generic copy like any other failure.
          const apiError = toApiError(error);
          this.titleError =
            apiError.isConflict && apiError.details
              ? apiError.details
              : 'An error occurred while updating. Please try again.';
        },
      });
  }

  // --- Members panel data source ---

  /**
   * Build the data source that drives the shared resource-membership panel. Sync-managed
   * members (sourced from the STB employee record) are non-removable; manually-added members
   * can be removed.
   */
  private buildPracticeAreaDataSource(
    code: string,
  ): ResourceMembershipDataSource {
    return {
      canManage: this.authService.canManagePracticeAreas(),
      lockedTooltip:
        "This resource's practice area is sourced from the STB employee record which is controlled outside of this application, so it cannot be removed.",
      addErrorMessage: 'Failed to add resource to practice area',
      removeErrorMessage: 'Failed to remove resource from practice area',
      loadAssigned: (page, pageSize, search) =>
        this.practiceAreaService.getAssignedResources(
          code,
          page,
          pageSize,
          search,
        ),
      searchCandidates: (page, pageSize, search) =>
        this.practiceAreaService.searchResourcesForAdmin({
          page,
          pageSize,
          search,
          excludePracticeAreaCode: code,
        }),
      add: (resource) =>
        this.practiceAreaService.addEmployeeToPracticeArea(
          resource.employeeId,
          code,
        ),
      remove: (resource) =>
        this.practiceAreaService.removeEmployeeFromPracticeArea(
          resource.employeeId,
          code,
        ),
      isRemovable: (resource) => {
        const pa = resource.practiceAreas.find((p) => p.practiceCode === code);
        // Positive test on purpose: only an explicitly Manual assignment is removable. Asking
        // instead whether the source is *not* EmployeeSync fails open — a missing or unrecognised
        // value reads as removable, so the Remove button appears on a sync-managed assignment and
        // the server rejects the click with a 400. That is reachable without any bug here: a
        // browser holding the previous bundle after a deploy sees a field it does not know about.
        // Failing closed hides a button instead of offering a broken one.
        return pa?.assignmentSource === EmployeePracticeAreaSource.Manual;
      },
    };
  }
}
